import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, BarChart3, BriefcaseBusiness, Check, Eye, FileText, Gem, Globe, GraduationCap, Heart,
  Mic2, Monitor, Network, Puzzle, Quote, Rocket, ShieldCheck, Star, Target,
  Users,
} from 'lucide-react';
import { IMG, REASONS } from '../mock';
import CTASection from '../components/CTASection';
import './MCEMedia.css';
import kebaPng from '../assets/keba.jpeg';
import womenPng from '../assets/women-build-africa-red.png';
import sparksPng from '../assets/networking/inspi8.jpeg';

const ICONS = { Target, Eye, Gem, Users, ShieldCheck, Heart, Puzzle, BarChart3, Globe };

const FOUNDER_POINTS = [
  'Après plus de 20 ans entre management, développement commercial, recrutement et leadership stratégique en France et au Maroc, Mounia Amane a créé MCE avec une conviction claire : les organisations grandissent mieux lorsqu’elles investissent dans l’humain, dans la structuration et dans l’innovation.',
  'MCE développe des passerelles entre la France, le Sénégal, le Maroc et l’Afrique de l’Ouest pour structurer les compétences, professionnaliser les organisations, accompagner l’emploi, l’entrepreneuriat et la transformation digitale.',
  'MCE ne propose pas des solutions génériques : elle crée des parcours concrets et sur mesure : formation, accompagnement RH, recrutement, coaching, outils digitaux, mise en réseau, entrepreneuriat féminin, employabilité des jeunes et initiatives diasporas.',
];

const FOUNDATIONS = [
  ['NOTRE VISION', 'Devenir le connecteur de référence entre l’Europe, le Maroc et l’Afrique de l’Ouest pour les talents et la transformation des organisations.', Eye],
  ['NOTRE MISSION', 'Apporter des solutions concrètes aux entreprises, institutions, ONG, écoles, universités et entrepreneurs pour structurer, former, recruter, digitaliser et accélérer leur croissance.', Target],
  ['NOS VALEURS', 'Excellence, transmission, proximité, innovation, inclusion et impact mesurable.', Gem],
];

const SOLUTIONS = [
  ['FORMATION & MCE ACADEMY', 'Des parcours de formation certifiants et sur mesure pour monter en compétences, structurer et faire évoluer les organisations.', GraduationCap],
  ['TALENTS & RH', 'Recrutement, évaluation, formation continue et développement des compétences pour libérer le potentiel humain.', Users],
  ['ACCOMPAGNEMENT STRATÉGIQUE', 'Conseil et appui stratégique pour structurer, piloter et transformer durablement les organisations.', Target],
  ['SOLUTIONS DIGITALES', 'Outils et solutions digitales pour optimiser les processus, améliorer la performance et accélérer la transformation.', Monitor],
  ['ÉVÉNEMENTS & IMPACT', 'Conférences, forums, ateliers et campagnes à fort impact pour inspirer, connecter et mobiliser les écosystèmes.', Mic2],
];

const COUNTRIES = [
  {
    name: 'FRANCE',
    role: 'Siège & Direction\nStratégie, partenariats internationaux',
    flag: '/assets/mce-about/flag-france-v2.jpg',
    accent: '#146B99',
    items: ['Conseil stratégique & accompagnement des entreprises', 'Solutions digitales & communication', 'Formations & développement des talents', 'Mise en réseau & partenariats'],
  },
  {
    name: 'SÉNÉGAL',
    role: 'Hub Afrique de l’Ouest\nOpérations & Impact',
    flag: '/assets/mce-about/flag-senegal-v2.jpg',
    accent: '#0F8B83',
    items: ['Accompagnement des PME & start-up', 'Développement des compétences et formations', 'Événements, networking & impact social', 'Recrutement & placement de talents'],
  },
  {
    name: 'MAROC',
    role: 'Partenariats &\nDéveloppement Régional',
    flag: '/assets/mce-about/flag-maroc-v2.jpg',
    accent: '#E45B2E',
    items: ['Conseil & accompagnement stratégique', 'Transformation digitale & communication', 'Partenariats institutionnels & privés', 'Programmes de formation & RH'],
  },
];

const PARTNERS = [
  {
    name: 'KEBA CONSULTING',
    desc: 'Former les leaders d’aujourd’hui pour bâtir le monde de demain.',
    galleryPartner: 'Keba Consulting',
    url: 'https://keba-consulting.com/',
    img: kebaPng,
    objectFit: 'cover',
    objectPosition: 'center 25%',
  },
  {
    name: 'WOMEN BUILD AFRICA',
    desc: 'Valoriser la femme, soutenir le local et faire grandir nos communautés.',
    galleryPartner: 'GoFar Holding',
    url: 'https://womenbuildafrica.com/',
    img: womenPng,
    objectFit: 'cover',
    objectPosition: 'center 32%',
  },
  {
    name: 'SPARKS PROJECT',
    desc: 'Innover, former et transformer pour construire les solutions de demain.',
    galleryPartner: 'Sparks Project',
    url: 'https://sparks-project.com/fr',
    img: sparksPng,
    objectFit: 'cover',
    objectPosition: 'center 10%',
  },
];

const REVIEWS = [
  {
    name: 'Aurelie Goubier',
    date: 'Juillet 2025',
    url: 'https://www.google.com/maps/contrib/104142318056676824561/reviews?hl=fr',
    text: 'Mounia est dynamique, ambitieuse, sérieuse et consciencieuse. Elle a une empathie extraordinaire, donne toujours de sa personne pour satisfaire les autres et son énergie boost toute une troupe ! Les services MCE sont irréprochables, réactifs et répondent aux besoins des clients. Foncez vous ne serez pas déçu !',
  },
  {
    name: 'Lili Lili',
    date: 'Octobre 2024',
    text: 'Je ne peux que recommander les services de cette entreprise. L’équipe est extrêmement professionnelle, réactive et à l’écoute des besoins de ses clients. Leurs attentions aux détails et leur capacité à anticiper les imprévus ont été impressionnantes. Chaque étape du processus a été gérée avec soin, ce qui a rendu la collaboration agréable et sans stress.',
  },
  {
    name: 'Aurélie Robin',
    date: 'Octobre 2024',
    text: 'Mounia est une conseillère en communication, très humaine, professionnelle, elle saura vous orienter vers l’outil le mieux adapté, afin de faire évoluer votre société, comme elle a sût si bien faire avec la mienne, je ne la remercierais jamais assez d’avoir eu les bons mots au bon moment. Vous pouvez lui faire confiance les yeux fermés 😊',
  },
  {
    name: 'RMNOUR de Rosa Kazanci',
    date: 'Mai 2024',
    text: 'Dès le premier contact, elle a été à l’écoute de mes besoins, m’accompagnant à chaque étape. Son dévouement et son professionnalisme m’ont vraiment impressionné. Je la recommande vivement à ceux qui recherchent un service personnalisé et de qualité. Vous ne serez pas déçu !',
  },
  {
    name: 'Sakina',
    date: 'Mai 2024',
    text: 'J’ai eu l’occasion de collaborer avec MCE et j’en suis pleinement satisfaite. Le résultat était au-delà de mes attentes. Les conseils donnés par Mounia et son travail sont au top. Je ne peux que recommander MCE. Bienveillance, écoute, réactivité et conseils sont les qualités que j’ai pu voir en faisant appel à MCE.',
  },
  {
    name: 'LA CLINIQUE AUTO',
    date: 'Décembre 2025',
    text: 'Excellent accompagnement du début à la fin. Site parfait, design et performant. Merci',
  },
  {
    name: 'B Moubarik',
    date: 'Juin 2024',
    text: 'Quelle bonheur de travailler avec MCE ! Equipe dynamique, efficace et professionnelle. Votre business se transformera et votre marge croîtra. Je recommande vivement !',
  },
  {
    name: 'Mithi BO',
    date: 'Mai 2024',
    text: 'Enfin une société sérieuse sur qui ont peut compter... Merci à Mounia pour son professionnalisme, ces conseils précieux et sa présence. N’hésitez pas...',
  },
  {
    name: 'GMA de Berkane Amine',
    date: 'Mai 2024',
    text: 'Un grand merci à la société MCE ! Plus particulièrement la Gérante Mounia ! Très professionnelle, bienveillante et surtout disponible. Merci pour tout !',
  },
  {
    name: 'Rkia Zirar',
    date: 'Mai 2024',
    text: 'Une collaboration pour la création d’un nouveau site que je recommande grandement ! Conseil, bienveillance et professionnalisme.',
  },
  {
    name: 'fleurs du moulin de Christine Baranger',
    date: 'Juillet 2024',
    url: 'https://www.google.com/maps/contrib/102970826365543566834/reviews?hl=fr',
    text: 'Merci à l’équipe MCE de m’avoir écoutée. Une très bonne collaboration. Très professionnelle, sérieuse, à l’écoute de nos besoins et réactive à tout moment. Je recommande sans problème.',
  },
  {
    name: 'Hamdouni Wafa',
    date: 'Mai 2024',
    text: 'Mouna est une personne qui est très à l\'écoute de ses clients réactive et soucieuse de produire un travail soigné. Je vous la recommande ++++',
  },
  {
    name: 'Hiba Doudar',
    date: 'Mars 2024',
    url: 'https://www.google.com/maps/contrib/105712461894677453276/reviews?hl=fr',
    text: 'Merci à l’équipe MCE pour leur professionnalisme et leur réactivité ! Je recommande vivement !',
  },
  {
    name: 'Bulteau Jérome',
    date: 'Mai 2024',
    text: 'Personne vraiment au top qui aime son métier, qui est là pour accompagner ses clients et qui donne des conseils. Je la recommande fortement.',
  },
  {
    name: 'Rayane Louglayal',
    date: 'Mai 2024',
    text: 'Je recommande fortement !!!! Très à l’écoute et très professionnel rien à dire !! Juste top',
  },
  {
    name: 'Nelly Kh',
    date: 'Mai 2024',
    text: 'Société à l\'écoute, conseils professionnels, une gestion et un service au top. Je recommande vivement.',
  },
  {
    name: 'Mohamadou M',
    date: 'Mai 2024',
    text: 'Merci encore pour les conseils et tout le travail fourni, un vrai plaisir de collaborer avec vous 😁',
  },
  {
    name: 'Elodie Chahbani',
    date: 'Mai 2024',
    text: 'Très professionnel, une vrai aide, très compréhensive et agréable !',
  },
];

export default function MCEPartenaires() {
  const [reviewIndex, setReviewIndex] = useState(0);
  const review = REVIEWS[reviewIndex];

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timer = window.setInterval(() => {
      setReviewIndex((index) => (index + 1) % REVIEWS.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <main className="pt-[72px] mce-about-page">
      <section className="founder-section">
        <div className="founder-shell">
          <figure>
            <img src="/assets/mce-about/mounia-amane-v2.jpg" alt="Mounia Amane, Fondatrice et Directrice Générale de MCE Group" />
            <figcaption><strong>Mounia Amane</strong><span>Fondatrice & Directrice Générale – MCE Group</span></figcaption>
          </figure>
          <div className="founder-copy">
            <h2>Le capital humain <em>au cœur d’une</em> croissance durable.</h2>
            <div className="founder-points">
              {FOUNDER_POINTS.map((text, index) => <article key={text}><i>{index + 1}</i><p>{text}</p></article>)}
            </div>
          </div>
          <blockquote>« Notre rôle est de révéler les potentiels, de créer des passerelles et de transformer les ambitions en leviers de performance et d’impact »</blockquote>
          <div className="founder-foundations">
            {FOUNDATIONS.map(([title, text, Icon]) => <article key={title}><Icon /><h3>{title}</h3><p>{text}</p></article>)}
          </div>
        </div>
      </section>

      <section className="mce-presentation">
        <div className="mce-about-container">
          <header>
            <h2>MCE, un partenaire de structuration, de <em>transformation</em> et d’<em>impact.</em></h2>
            <p>MCE accompagne entreprises, PME, institutions, ONG, écoles, universités, entrepreneurs, jeunes et femmes porteuses de projet avec des solutions concrètes pour développer les talents, renforcer les organisations et accélérer une croissance durable et inclusive.</p>
          </header>
          <div className="mce-solutions-title"><span>UN ÉCOSYSTÈME DE SOLUTIONS</span><b>AU SERVICE DE LA CROISSANCE</b></div>
          <div className="mce-solutions-grid">
            {SOLUTIONS.map(([title, text, Icon], index) => <article key={title} style={{ '--solution-index': index }}><Icon /><h3>{title}</h3><p>{text}</p></article>)}
          </div>
        </div>
      </section>

      <section className="presence-section">
        <div className="mce-about-container">
          <div className="presence-header-grid">
            <header>
              <h2>NOTRE PRÉSENCE<br /><span>INTERNATIONALE</span></h2>
              <b>Trois régions clés. Une vision globale.<br />Un impact local.</b>
              <i />
              <p>MCE intervient en France, au Sénégal et au Maroc pour accompagner les entreprises, institutions et talents dans leurs projets de développement.</p>
            </header>
            <figure className="presence-flags">
              <img src="/assets/mce-about/international-flags-v2.png" alt="Drapeaux de la France, du Sénégal et du Maroc" />
            </figure>
          </div>

          <div className="presence-map-row">
            <figure className="presence-map">
              <img src="/assets/mce-about/international-map-v2.jpg" alt="Carte de la présence internationale de MCE" />
              <span className="presence-map-point point-france" aria-label="France" />
              <span className="presence-map-point point-senegal" aria-label="Sénégal" />
              <span className="presence-map-point point-maroc" aria-label="Maroc" />
            </figure>
            <aside className="presence-expertise">
              <Globe />
              <p>Une expertise locale,<br />une vision globale,<br />des solutions adaptées<br />à chaque marché.</p>
            </aside>
          </div>

          <div className="presence-country-grid">
            {COUNTRIES.map(country => (
              <article key={country.name} style={{ '--country-accent': country.accent }}>
                <div className="presence-country-title">
                  <img src={country.flag} alt={`Drapeau ${country.name}`} />
                  <div><h3>{country.name}</h3><p>{country.role}</p></div>
                </div>
                <ul>{country.items.map(item => <li key={item}><Check />{item}</li>)}</ul>
              </article>
            ))}
          </div>

          <div className="presence-principles">
            <article><Globe /><b>UNE APPROCHE MULTICULTURELLE</b><p>Comprendre chaque contexte pour agir efficacement.</p></article>
            <article><Target /><b>DES SOLUTIONS SUR-MESURE</b><p>Adaptées aux réalités locales et aux ambitions internationales.</p></article>
            <article><Network /><b>UN RÉSEAU SOLIDE</b><p>Entreprises, institutions, experts et talents partout où nous sommes.</p></article>
            <article><BarChart3 /><b>UN IMPACT DURABLE</b><p>Créer de la valeur, générer des opportunités et bâtir des relations de confiance.</p></article>
          </div>

          <div className="presence-stats">
            <div className="presence-stats-intro">MCE, votre partenaire<br />de proximité pour des<br />projets à portée mondiale.<i /></div>
            <article><Globe /><b>3</b><span>PAYS<br />D’IMPLANTATIONS</span></article>
            <article><Users /><b>+50</b><span>CLIENTS<br />ACCOMPAGNÉS</span></article>
            <article><BriefcaseBusiness /><b>+20</b><span>PARTENAIRES<br />STRATÉGIQUES</span></article>
            <article><BarChart3 /><b>100%</b><span>ENGAGEMENT POUR<br />VOS SUCCÈS</span></article>
            <div className="presence-mobile-landmarks" aria-label="Présence de MCE en France, au Sénégal et au Maroc">
              <figure><img src="/assets/mce-about/mobile-france.jpg" alt="Tour Eiffel, France" /><figcaption>FRANCE</figcaption></figure>
              <figure><img src="/assets/mce-about/mobile-senegal.jpg" alt="Monument de la Renaissance africaine, Sénégal" /><figcaption>SÉNÉGAL</figcaption></figure>
              <figure><img src="/assets/mce-about/mobile-maroc.jpg" alt="Mosquée Hassan II, Maroc" /><figcaption>MAROC</figcaption></figure>
            </div>
          </div>
        </div>
      </section>

      <section className="mce-media" aria-labelledby="mce-media-title">
        <div className="mce-about-container mce-media-shell">
          <div className="mce-media-copy">
            <h2 id="mce-media-title">Communication & Médias <br />Notre mission, votre visibilité.</h2>
            <p>Découvrez la vision de Mounia Amane, fondatrice de l’agence MCE, et son engagement à amplifier la présence de chaque partenaire à travers des solutions médias innovantes et sur mesure.</p>
            <div className="mce-media-actions">
              <a className="mce-media-button mce-media-youtube" href="https://www.youtube.com/@MCEAgency" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M23 7a3 3 0 0 0-2-2C19 4.5 12 4.5 12 4.5S5 4.5 3 5a3 3 0 0 0-2 2 31 31 0 0 0-.5 5 31 31 0 0 0 .5 5 3 3 0 0 0 2 2c2 .5 9 .5 9 .5s7 0 9-.5a3 3 0 0 0 2-2 31 31 0 0 0 .5-5A31 31 0 0 0 23 7Z" /><path fill="#d71920" d="m10 8 6 4-6 4Z" /></svg>
                S'abonner à notre chaîne
              </a>
            </div>
          </div>
          <figure className="mce-media-video">
            <div className="relative aspect-video overflow-hidden bg-mce-navy">
            <iframe
              className="absolute inset-0 h-full w-full border-0"
              src="https://www.youtube.com/embed/C8B-STYQCuY?si=R16oNF73hyYEtm4D"
              title="Vidéo de présentation MCE"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
            </div>
            <figcaption><strong>Interview | Mounia Amane, fondatrice de MCE</strong><span>MCE Agency · YouTube</span></figcaption>
          </figure>
        </div>
      </section>

      <section className="mce-reasons">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <header className="mce-reasons-heading"><b>POURQUOI CHOISIR MCE ?</b><span>PLUS QU’UN PRESTATAIRE, UN VRAI PARTENAIRE.</span></header>
          <div className="mce-reasons-grid">
            {REASONS.map((reason) => {
              const Icon = ICONS[reason.icon];
              return <article key={reason.title}><Icon /><b>{reason.title}</b><p>{reason.desc}</p></article>;
            })}
          </div>
        </div>
      </section>

      <section className="mce-reviews" aria-labelledby="mce-reviews-title">
        <div className="mce-reviews-shell">
          <header>
            <h2 id="mce-reviews-title">Ils ont choisi MCE.<br /><em>Ils racontent leur expérience.</em></h2>
            <p>Des collaborations fondées sur l’écoute, la réactivité et la qualité du travail accompli.</p>
          </header>

          <div className="mce-review-stage" key={review.name}>
            <Quote className="mce-review-quote" aria-hidden="true" />
            <div className="mce-review-stars" aria-label="5 étoiles sur 5">
              {[1, 2, 3, 4, 5].map((star) => <Star key={star} />)}
            </div>
            <blockquote>{review.text}</blockquote>
            <footer>
              <span>{review.name.split(' ').map((part) => part[0]).join('').slice(0, 2)}</span>
              <div>
                {review.url
                  ? <a href={review.url} target="_blank" rel="noopener noreferrer">{review.name}</a>
                  : <b>{review.name}</b>}
                <small>5 étoiles · {review.date}</small>
              </div>
            </footer>
          </div>

          <nav className="mce-review-controls" aria-label="Navigation entre les avis clients">
            <button type="button" onClick={() => setReviewIndex((reviewIndex - 1 + REVIEWS.length) % REVIEWS.length)} aria-label="Avis précédent"><ArrowLeft /></button>
            <div>{REVIEWS.map((item, index) => <button type="button" key={item.name} className={index === reviewIndex ? 'active' : ''} onClick={() => setReviewIndex(index)} aria-label={`Afficher l’avis de ${item.name}`} aria-current={index === reviewIndex ? 'true' : undefined} />)}</div>
            <span>{String(reviewIndex + 1).padStart(2, '0')} / {String(REVIEWS.length).padStart(2, '0')}</span>
            <button type="button" onClick={() => setReviewIndex((reviewIndex + 1) % REVIEWS.length)} aria-label="Avis suivant"><ArrowRight /></button>
          </nav>
        </div>
      </section>

      <CTASection title="ENSEMBLE, CRÉONS L’AVENIR" subtitle="DE VOTRE ORGANISATION." />
    </main>
  );
}
